import React from "react";
import { SOURCE } from "./approvalRuleFormat";
import "../../../components/ui/HierarchyChart.css";

/** "Ana is on leave: Ben covers" lines for one level (byType[].delegations). */
const Covers = ({ delegations, level }) =>
  (delegations || [])
    .filter((d) => d.level === level)
    .map((d) => (
      <div
        key={`${d.onLeave?.id}-${d.delegate?.id}`}
        className="fs-070 text-warning-emphasis"
      >
        <i className="bi bi-person-dash me-1"></i>
        {d.onLeave?.name} is on leave: {d.delegate?.name} covers
      </div>
    ));

/** Employee → Level 1 → Level 2 … as connected cards (one request type). */
const Chain = ({ employeeName, levels, delegations }) => (
  <ol className="hc-chain mt-1">
    <li>
      <div className="hc-card">
        <div className="hc-card-sub">Employee</div>
        <div className="hc-card-title">{employeeName}</div>
      </div>
    </li>
    {levels.map((l) => (
      <li key={l.level}>
        <div className="hc-card">
          <div className="hc-card-sub">Level {l.level}</div>
          {(l.approvers || []).length === 0 ? (
            <div className="hc-card-sub hc-warn">Nobody fits (skipped)</div>
          ) : (
            l.approvers.map((a, i) => (
              <div key={a.id} className="fs-080">
                {i > 0 && (
                  <span className="text-slate-400 fs-070 d-block">or</span>
                )}
                <strong className="text-navy">{a.name}</strong>
                {a.branchName && (
                  <span className="text-slate-500 fs-070">
                    {" "}
                    · {a.branchName}
                  </span>
                )}
              </div>
            ))
          )}
          <Covers delegations={delegations} level={l.level} />
        </div>
      </li>
    ))}
  </ol>
);

const EMPTY = [];

/**
 * Who approves each request type for one employee:
 * byType = [{ requestType, label, source: EMPLOYEE | RULE | DEFAULT, levels: [{ level, approvers }] }].
 * chainFor = employee name → each type drawn as Employee → Level 1 → Level 2 cards.
 */
const EffectiveApprovers = ({ byType = EMPTY, compact, chainFor }) =>
  byType.length === 0 ? (
    <p className="fs-080 text-slate-500 mb-0">No routing information.</p>
  ) : (
    <div
      className={`rule-effective ${compact ? "rule-effective--compact" : ""}`}
    >
      {byType.map((t) => (
        <div key={t.requestType} className="rule-effective-type">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
            <strong className="fs-085">{t.label || t.requestType}</strong>
            <span
              className={`status-pill status-pill--${SOURCE[t.source]?.tone || "gray"}`}
            >
              {SOURCE[t.source]?.label || t.source}
            </span>
          </div>
          {t.source === "DEFAULT" || (t.levels || EMPTY).length === 0 ? (
            <div className="fs-080 text-slate-500">
              Branch managers up the branch tree, plus HR.
            </div>
          ) : chainFor ? (
            <Chain
              employeeName={chainFor}
              levels={t.levels}
              delegations={t.delegations}
            />
          ) : (
            <ol className="rule-effective-levels">
              {t.levels.map((l) => (
                <li key={l.level}>
                  <span className="text-slate-500 me-1">Level {l.level}:</span>
                  {(l.approvers || EMPTY).length === 0 ? (
                    <span className="text-slate-400">
                      nobody fits (skipped)
                    </span>
                  ) : (
                    l.approvers.map((a, i) => (
                      <span key={a.id}>
                        {i > 0 && <span className="text-slate-400"> or </span>}
                        <span className="fw-semibold">{a.name}</span>
                        {a.branchName && (
                          <span className="text-slate-500 fs-075">
                            {" "}
                            ({a.branchName})
                          </span>
                        )}
                      </span>
                    ))
                  )}
                  <Covers delegations={t.delegations} level={l.level} />
                </li>
              ))}
            </ol>
          )}
        </div>
      ))}
    </div>
  );

export default EffectiveApprovers;
